import React from 'react';
import { motion } from 'framer-motion';
import { Container, Section, Badge } from '../ui';
import { TECH_STACK } from '../../constants';
import { useIntersectionObserver } from '../../hooks';

// Tech logos/icons would typically be imported images or SVGs
// Using text representations for simplicity
const techCategories = {
  'Frontend': ['React', 'Vue.js', 'Angular', 'Next.js', 'TypeScript'],
  'Backend': ['Node.js', 'Python', 'Java', 'Go', '.NET'],
  'Cloud': ['AWS', 'Azure', 'GCP', 'Kubernetes', 'Docker'],
  'Data': ['PostgreSQL', 'MongoDB', 'Redis', 'Kafka', 'Spark'],
  'AI/ML': ['TensorFlow', 'PyTorch', 'OpenAI', 'LangChain', 'Hugging Face'],
};

export const TechStack: React.FC = () => {
  const { elementRef, isVisible } = useIntersectionObserver({ threshold: 0.1 });

  return (
    <Section background="gray" padding="lg" id="tech-stack">
      <Container>
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <Badge variant="primary" className="mb-4">Technology Stack</Badge>
          <h2 className="heading-lg text-navy-900 mb-4">
            Modern Technologies We Master
          </h2>
          <p className="text-body text-lg">
            We stay at the forefront of technology, leveraging the best tools 
            and frameworks to build scalable, efficient solutions.
          </p>
        </div>

        {/* Tech Categories */}
        <motion.div
          ref={elementRef}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          {Object.entries(techCategories).map(([category, techs], categoryIndex) => (
            <motion.div
              key={category}
              className="bg-white rounded-2xl p-6 border border-navy-100"
              initial={{ opacity: 0, y: 20 }}
              animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ delay: categoryIndex * 0.1 }}
            >
              <h3 className="font-semibold text-navy-900 mb-4 text-center">
                {category}
              </h3>
              <div className="space-y-2">
                {techs.map((tech, techIndex) => (
                  <motion.div
                    key={tech}
                    className="flex items-center justify-center py-2 px-3 bg-navy-50 rounded-lg text-sm text-navy-700 hover:bg-accent-50 hover:text-accent-700 transition-colors cursor-default"
                    initial={{ opacity: 0, x: -10 }}
                    animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: -10 }}
                    transition={{ delay: categoryIndex * 0.1 + techIndex * 0.05 }}
                  >
                    {tech}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Scrolling Tech Strip */}
        <div className="mt-12 overflow-hidden relative">
          <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-gray-100 to-transparent z-10" />
          <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-gray-100 to-transparent z-10" />
          <div className="flex animate-scroll whitespace-nowrap">
            {[...TECH_STACK, ...TECH_STACK].map((tech, index) => (
              <span
                key={`${tech}-${index}`}
                className="mx-8 text-2xl font-semibold text-navy-200 hover:text-navy-500 transition-colors cursor-default"
              >
                {tech}
              </span>
            ))}
          </div>
        </div>
      </Container>
    </Section>
  );
};

